import { Component, OnInit, ViewChildren, AfterViewInit, OnDestroy, TemplateRef, ViewChild, ElementRef } from '@angular/core';
import { BaseComponent } from 'src/app/shared/components/base/base.component';
import { WarningComponent } from 'src/app/shared/components/warning/warning.component';
import { SCREEN_SIZE } from 'src/app/core/constants/screen-size';
import { MatSort, Sort } from '@angular/material/sort';
import { DataSource } from '@angular/cdk/collections';
import { merge, Observable, BehaviorSubject, Subscription } from 'rxjs';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Message } from 'src/app/core/class/message';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from 'src/app/core/services/common.service';
import { MatTableDataSource } from '@angular/material/table';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { ResponsiveService } from '../../../../../../core/services/responsive.service';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { DigitalCertificateService } from 'src/app/core/services/digital-certificate.service';
import { EnumSignMethod } from '../../../../../../core/constants/contract';
import { TruncatePipe } from 'src/app/shared/pipe/TruncatePipe';
import { Inject, Optional } from '@angular/core';
import { SignDocumentService } from 'src/app/core/services/sign-document.service';
import {ContractService} from 'src/app/core/services/contract.service';
import { AsEnterpriseService } from 'src/app/core/services/as-enterprise.service';

@Component({
  selector: 'app-share-resource',
  templateUrl: './share-resource.component.html',
  styleUrls: ['./share-resource.component.scss']
})

export class ShareResourceComponent extends BaseComponent implements OnInit, AfterViewInit {
  @ViewChild('contractInput', { read: ElementRef, static: false }) contractInputRef: ElementRef;
  @ViewChild('smsInput', { read: ElementRef, static: false }) smsInputRef: ElementRef;

  contractValue: number = 0;
  smsValue: number = 0;
  remainingContract: number = 0;
  remainingSms: number = 0;

  contractError: string = '';
  smsError: string = '';
  maxContract: number = 0;
  maxSms: number = 0;

  loadResourceError: boolean = false;
  isContractExpired: boolean = false;
  isSmsExpired: boolean = false;
  expiredResourceMessage: string = '';
  maxContractDisplay: string = '--';
  maxSmsDisplay: string = '--';
  remainingContractDisplay: string = '--';
  remainingSmsDisplay: string = '--';


  private isComposing: boolean = false;

  @BlockUI() blockUI: NgBlockUI;

  constructor(
    private dialog: MatDialog,
    public commonService: CommonService,
    public message: Message,
    private toastr: ToastrService,
    private responsiveService: ResponsiveService,
    private signDocumentService: SignDocumentService,
    private contractService: ContractService,
    private asEnterpriseService: AsEnterpriseService,
    @Optional() public dialogRef: MatDialogRef<ShareResourceComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super(commonService, message);

  }
  ngOnInit(): void {
    this.expiredResourceMessage = this.message.getMessage('resource_transfer.expired_resource_message');
    this.loadAvailableResources();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const placeholderText = this.message.getMessage('resource_transfer.place_holder');
      this.setInnerPlaceholder(this.contractInputRef, placeholderText);
      this.setInnerPlaceholder(this.smsInputRef, placeholderText);
    });
  }

  private setInnerPlaceholder(ref: ElementRef, text: string): void {
    if (!ref || !ref.nativeElement) return;
    const host = ref.nativeElement as HTMLElement;
    const inner = host instanceof HTMLInputElement
      ? host
      : (host.querySelector ? host.querySelector('input') : null);
    if (inner) {
      (inner as HTMLInputElement).setAttribute('placeholder', text || '');
    }
  }

  formatterNumber = (value: number | string): string => {
    if (value === null || value === undefined || value === '') return '';
    let str = String(value).replace(/\D/g, '');
    return str.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  parserNumber = (value: string): string => {
    return value ? value.replace(/,/g, '') : '';
  };

  onCompositionStart(): void {
    this.isComposing = true;
  }

  onContractCompositionEnd(event: Event): void {
    this.isComposing = false;
    this.onContractInput(event);
  }

  onSmsCompositionEnd(event: Event): void {
    this.isComposing = false;
    this.onSmsInput(event);
  }

  private findInput(event: Event): HTMLInputElement | null {
    const target = event.target as HTMLElement;
    if (!target) return null;
    if (target instanceof HTMLInputElement) return target;
    const inner = target.querySelector ? (target.querySelector('input') as HTMLInputElement | null) : null;
    return inner || null;
  }

  private applyLiveFormat(input: HTMLInputElement): string {
    let raw = String(input.value != null ? input.value : '');
    let digits = raw.replace(/\D/g, '');
    if (digits.length > 1 && /^0+$/.test(digits)) {
      digits = '0';
      raw = '0';
    }
    const formatted = digits === '' ? '' : digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const oldLen = raw.length;
    const cursor = input.selectionStart != null ? input.selectionStart : formatted.length;
    if (input.value !== formatted) {
      input.value = formatted;
      const newCursor = Math.max(0, cursor + (formatted.length - oldLen));
      try { input.setSelectionRange(newCursor, newCursor); } catch (e) { }
    }
    return digits;
  }

  private clampToMax(input: HTMLInputElement, num: number, max: number): number {
    if (num > max) {
      const formatted = this.formatterNumber(max);
      input.value = formatted;
      try { input.setSelectionRange(formatted.length, formatted.length); } catch (e) { }
      return max;
    }
    if (num < 0) {
      const formatted = this.formatterNumber(0);
      input.value = formatted;
      try { input.setSelectionRange(formatted.length, formatted.length); } catch (e) { }
      return 0;
    }
    return num;
  }

  onContractInput(event: Event): void {
    if (this.isContractExpired) return;
    if (this.isComposing && event.type === 'input') return;
    const input = this.findInput(event);
    if (!input) return;
    const digits = this.applyLiveFormat(input);
    const num = digits === '' ? 0 : Number(digits);
    this.contractValue = this.clampToMax(input, num, this.maxContract);
    this.calculateRemaining();
  }

  onSmsInput(event: Event): void {
    if (this.isSmsExpired) return;
    if (this.isComposing && event.type === 'input') return;
    const input = this.findInput(event);
    if (!input) return;
    const digits = this.applyLiveFormat(input);
    const num = digits === '' ? 0 : Number(digits);
    this.smsValue = this.clampToMax(input, num, this.maxSms);
    this.calculateRemaining();
  }

  stepContract(delta: number): void {
    if (this.isContractExpired) return;
    const current = Number(this.contractValue) || 0;
    const next = Math.max(0, Math.min(this.maxContract, current + delta));
    this.contractValue = next;
    this.calculateRemaining();
  }

  stepSms(delta: number): void {
    if (this.isSmsExpired) return;
    const current = Number(this.smsValue) || 0;
    const next = Math.max(0, Math.min(this.maxSms, current + delta * 1000));
    this.smsValue = next;
    this.calculateRemaining();
  }

  loadAvailableResources() {
    this.contractService.getDataCountNumberDocumentOfEnterpriseByService().subscribe(
      (response: any) => {
        if (response && response.data) {
          const data = response.data;
          
          this.loadResourceError = false;
          this.maxContract = Number(data.contractQuantity) || 0;
          this.maxSms = Number(data.smsTotalMoney) || 0;
  
          const expireDate = data.extendedExpireDateContractQuantity;
          const isExpired = this.checkExpired(expireDate);
          
          this.isContractExpired = isExpired;
          this.isSmsExpired = isExpired;
  
          if (this.isContractExpired) {
            this.contractValue = 0;
          } else if (this.contractValue > this.maxContract) {
            this.contractValue = 0;
          }
  
          if (this.isSmsExpired) {
            this.smsValue = 0;
          } else if (this.smsValue > this.maxSms) {
            this.smsValue = 0;
          }
          
        } else {
          this.setLoadResourceError();
        }
        this.calculateRemaining();
      },
      (err: any) => {
        this.setLoadResourceError();
        this.calculateRemaining();
      }
    );
  }

  private checkExpired(expireDateStr: string): boolean {
    if (!expireDateStr) return false;
    const dt = new Date(expireDateStr);
    if (isNaN(dt.getTime())) return false;
    return dt.getTime() < Date.now();
  }
  
  private setLoadResourceError(): void {
    this.loadResourceError = true;
    this.maxContract = 0;
    this.maxSms = 0;
    this.contractValue = 0;
    this.smsValue = 0;
  }

  private formatDisplayNumber(value: number): string {
    if (value === null || value === undefined || isNaN(value)) return '--';
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  calculateRemaining() {
    const val = Number(this.contractValue) || 0;

    if (val > this.maxContract) {
      this.contractError = this.message.getMessage('resource_transfer.error_max');
    } else if (val < 0) {
      this.contractError = this.message.getMessage('resource_transfer.error_positive_integer');
    } else {
      this.contractError = '';
    }

    this.remainingContract = this.maxContract - val;

    if (this.smsValue > this.maxSms) {
      this.smsError = this.message.getMessage('resource_transfer.error_max');
    } else if (this.smsValue < 0) {
      this.smsError = this.message.getMessage('resource_transfer.error_positive_integer');
    } else {
      this.smsError = '';
    }
    this.remainingSms = this.maxSms - (this.smsValue || 0);
    if (this.loadResourceError) {
      this.maxContractDisplay = '--';
      this.maxSmsDisplay = '--';
      this.remainingContractDisplay = '--';
      this.remainingSmsDisplay = '--';
    } else {
      this.maxContractDisplay = this.formatDisplayNumber(this.maxContract);
      this.maxSmsDisplay = this.formatDisplayNumber(this.maxSms);
      this.remainingContractDisplay = this.formatDisplayNumber(this.remainingContract);
      this.remainingSmsDisplay = this.formatDisplayNumber(this.remainingSms);
    }
  }

  onSubmit() {
    this.calculateRemaining();
    if (this.contractError || this.smsError) {
      return;
    }
    const confirmMsg = this.message.getMessage('resource_transfer.confirm_msg');
    const noteMsg = this.message.getMessage('resource_transfer.note_warning');
    const dialogRef = this.dialog.open(WarningComponent, {
      width: '40%',
      data: confirmMsg + '\n' + noteMsg,
      panelClass: 'share-resource-warning'
    });

    const tryDecorate = (attempts: number) => {
      const panel = document.querySelector('.share-resource-warning');
      if (panel && panel.textContent && panel.textContent.includes(noteMsg)) {
        this.decorateWarningDialog(noteMsg);
      } else if (attempts < 10) {
        requestAnimationFrame(() => tryDecorate(attempts + 1));
      }
    };
    requestAnimationFrame(() => tryDecorate(0));

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.callShareResource();
      }
    });
  }

  private callShareResource(): void {
    const body = {
      contractQuantity: this.isContractExpired ? 0 : (Number(this.contractValue) || 0),
      smsQuantity: this.isSmsExpired ? 0 : (Number(this.smsValue) || 0)
    };

    this.blockUI.start();
    this.signDocumentService.shareResource(body).subscribe({
      next: (response: any) => {
        this.blockUI.stop();
        if (response && response.success === false) {
          const rawCode = (response && response.code)
            || (response && response.error && response.error.code)
            || '';
          const errCode = this.normalizeErrorCode(rawCode);
          const rawMsg = (response && response.message)
            || (response && response.error && response.error.message)
            || '';
          const failMsg = rawMsg
            ? rawMsg
            : this.message.getMessage('messages.update_failed');
          this.toastr.error(failMsg);
          if (errCode === '0x020332' || errCode === '0x020330') {
            this.loadAvailableResources();
          }
          return;
        }
        this.toastr.success(this.message.getMessage('resource_transfer.transfer_success'));
        this.contractValue = 0;
        this.smsValue = 0;
        this.loadAvailableResources();
      },
      error: (err: any) => {
        this.blockUI.stop();
        const body = (err && err.error && typeof err.error === 'object' && !Array.isArray(err.error))
          ? err.error
          : err;
        const rawCode = (body && body.code) || '';
        const errCode = this.normalizeErrorCode(rawCode);
        const rawMsg = (body && body.message) || '';
        const errMsg = rawMsg
          ? rawMsg
          : this.message.getMessage('messages.update_failed');
        this.toastr.error(errMsg);
        if (errCode === '0x020332' || errCode === '0x020330') {
          this.loadAvailableResources();
        }
      }
    });
  }
  
  private normalizeErrorCode(code: any): string {
    if (code === null || code === undefined) return '';
    return String(code).trim().toLowerCase();
  }
  onNoClick(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
  get isSubmitDisabled(): boolean {
    const c = Number(this.contractValue) || 0;
    const s = Number(this.smsValue) || 0;
    const canTransferContract = !this.isContractExpired && c > 0;
    const canTransferSms = !this.isSmsExpired && s > 0;
    return !(canTransferContract || canTransferSms);
  }

  private decorateWarningDialog(noteMsg: string): void {
    const panel = document.querySelector('.share-resource-warning');
    if (!panel || !noteMsg) return;

    const walker = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT, null as any);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const textNode = node as Text;
      const raw = textNode.nodeValue || '';
      const idx = raw.indexOf(noteMsg);
      if (idx === -1) continue;

      const parent = textNode.parentNode;
      if (!parent) continue;

      const before = raw.slice(0, idx).replace(/\s+$/, '');
      const frag = document.createDocumentFragment();
      if (before) {
        frag.appendChild(document.createTextNode(before));
      }
      const br = document.createElement('br');
      frag.appendChild(br);

      const noteEl = document.createElement('i');
      noteEl.className = 'share-resource-warning-note';
      noteEl.textContent = noteMsg;
      frag.appendChild(noteEl);

      parent.replaceChild(frag, textNode);
      break;
    }
  }
}
